<template>
  <div
    class="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800"
  >
    <HeaderDefault />

    <main
      class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 space-y-12 md:space-y-16"
    >
      <!-- Hero Section -->
      <div class="text-center space-y-4 md:space-y-6 max-w-4xl mx-auto">
        <h1
          class="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white tracking-tight"
        >
          Latest
          <span
            class="hover:animate-shake inline-block cursor-pointer text-primary"
          >
            Buzz
          </span>
          Posts
        </h1>
        <p
          class="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed"
        >
          Stay updated with the latest news, insights, and announcements from
          our academic community.
        </p>
      </div>
      <!-- filter post option thingy is here -->
      <div
        class="sticky top-4 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-md p-3 rounded-lg shadow-sm z-10 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
      >
        <!-- Category Filter Buttons -->
        <div class="flex flex-wrap gap-2 items-center">
          <span
            class="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1 shrink-0"
            >Filter by:</span
          >
          <button
            @click="selectCategory(null)"
            :class="[
              'px-3 py-1 rounded-full text-sm font-medium transition-colors duration-150 border',
              // Changed: rounded-full, adjusted padding, added border
              selectedCategorySlug === null
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500',
            ]"
          >
            All
            <!-- Changed: Shortened text for better fit -->
          </button>
          <button
            v-for="cat in categories"
            :key="cat.id"
            @click="selectCategory(cat.attributes.slug)"
            :class="[
              'px-3 py-1 rounded-full text-sm font-medium transition-colors duration-150 border',
              // Changed: rounded-full, adjusted padding, added border
              selectedCategorySlug === cat.attributes.slug
                ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-600 hover:border-gray-400 dark:hover:border-gray-500',
            ]"
          >
            {{ cat.attributes.name }}
          </button>
        </div>

        <!-- Sorting Dropdown -->
        <div class="flex justify-end sm:justify-start shrink-0">
          <!-- Changed: Removed justify-end on parent, added shrink-0 -->
          <div class="w-full sm:w-auto">
            <label for="sort-select" class="sr-only">Sort by</label>
            <select
              id="sort-select"
              v-model="currentSort"
              @change="handleFilterChange"
              class="block w-full pl-3 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-500 dark:focus:ring-gray-400 focus:border-gray-500 dark:focus:border-gray-400"
            >
              <option
                v-for="option in sortOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div
        v-if="isLoading"
        class="grid gap-8 sm:gap-10 md:grid-cols-1 lg:grid-cols-2"
      >
        <!-- Display a fixed number of skeletons (e.g., 4 for a 2-col layout) -->
        <div
          v-for="n in 4"
          :key="`skel-${n}`"
          class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 animate-pulse"
        >
          <div class="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
          <div
            class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"
          ></div>
          <div
            class="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"
          ></div>
          <div
            class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"
          ></div>
          <div
            class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6 mb-4"
          ></div>
          <div class="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-1/3"></div>
        </div>
      </div>

      <!-- Error State -->
      <div
        v-else-if="error"
        class="text-center py-16 px-6 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800"
      >
        <!-- Simple Error Icon Placeholder (e.g., using text or SVG) -->
        <span class="text-4xl mb-4 inline-block" role="img" aria-label="Error"
          >⚠️</span
        >
        <h3 class="text-xl font-semibold text-red-800 dark:text-red-300 mb-2">
          Oops! Something went wrong.
        </h3>
        <p class="text-red-600 dark:text-red-400 mb-4">{{ error }}</p>
        <!-- Standard HTML Button for Try Again -->
        <button
          @click="fetchBlogData"
          class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          <!-- Simple Icon Placeholder -->
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5 mr-2"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 110 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
              clip-rule="evenodd"
            />
          </svg>
          Try Again
        </button>
      </div>

      <!-- Blog Posts Grid & No Posts State -->
      <div v-else>
        <div
          v-if="posts.length > 0"
          class="grid gap-8 sm:gap-10 md:grid-cols-1 lg:grid-cols-2"
        >
          <BuzzCard
            v-for="post in posts"
            :key="post.id"
            :post="post"
            :isFeatured="false"
            class="transform transition-all duration-300 hover:-translate-y-1 hover:shadow-lg h-full flex flex-col"
          />
        </div>

        <!-- No Posts State -->
        <div
          v-else
          class="text-center py-20 px-4 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700"
        >
          <!-- Simple Icon Placeholder -->
          <span
            class="text-6xl mb-6 inline-block text-gray-400 dark:text-gray-500"
            role="img"
            aria-label="Magnifying glass"
            >🔍</span
          >
          <h3
            class="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-3"
          >
            No posts found
          </h3>
          <p class="text-gray-500 dark:text-gray-400 text-lg max-w-md mx-auto">
            {{
              selectedCategorySlug
                ? "There are no posts in this category yet."
                : "Try adjusting your filters or check back soon for new updates."
            }}
          </p>
          <!-- Standard HTML Button for Show All -->
          <button
            v-if="selectedCategorySlug"
            @click="selectCategory(null)"
            <!--
            Use
            selectCategory
            method
            --
          >
            class="mt-6 px-5 py-2 border border-transparent text-base
            font-medium rounded-md text-white bg-gray-800 hover:bg-gray-900
            dark:bg-gray-600 dark:hover:bg-gray-500 focus:outline-none
            focus:ring-2 focus:ring-offset-2 focus:ring-gray-500" > Show All
            Posts
          </button>
        </div>
      </div>

      <!-- Pagination -->
      <!-- Pagination -->
      <nav
        v-if="!isLoading && !error && posts.length > 0 && totalPages > 1"
        class="flex items-center justify-center pt-10 md:pt-16"
        aria-label="Pagination"
      >
        <!-- Previous Button -->
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="relative inline-flex items-center px-3 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150"
        >
          <span class="sr-only">Previous</span>
          <svg
            class="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>

        <!-- Page Numbers -->
        <template v-for="page in paginationRange" :key="page">
          <!-- Ellipsis -->
          <span
            v-if="page === '...'"
            class="relative inline-flex items-center px-4 py-2 border-y border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-700 dark:text-gray-400"
          >
            ...
          </span>
          <!-- Page Button -->
          <button
            v-else
            @click="goToPage(page as number)"
            :class="[
              'relative inline-flex items-center px-4 py-2 border text-sm font-medium transition-colors duration-150',
              page === currentPage
                ? 'z-10 bg-gray-900 border-gray-900 text-white dark:bg-white dark:border-white dark:text-gray-900 cursor-default'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
              page !== '...' ? 'border-l-0' : '', // Avoid double borders
            ]"
            :aria-current="page === currentPage ? 'page' : undefined"
          >
            {{ page }}
          </button>
        </template>

        <!-- Next Button -->
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="relative inline-flex items-center px-3 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 border-l-0"
        >
          <span class="sr-only">Next</span>
          <svg
            class="h-5 w-5"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </nav>
    </main>

    <FooterDefault />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useBuzzStore } from "~/stores/buzz";
import { ref, computed, onMounted, watch } from "vue";

// Initialize Store and State
const buzzStore = useBuzzStore();
const { posts, categories, pagination, isLoading, error } =
  storeToRefs(buzzStore);

// Local Reactive State for Controls
const currentPage = ref(1);
const selectedCategorySlug = ref<string | null>(null);
const currentSort = ref("publishedAt:desc");

// Options for Select Menus
const sortOptions = [
  { label: "Newest First", value: "publishedAt:desc" },
  { label: "Oldest First", value: "publishedAt:asc" },
];

// Computed total pages based on store pagination info
const totalPages = computed(() => pagination.value?.pageCount || 1);

// Fetching Logic (Modified parameters)
const fetchBlogData = async (page = currentPage.value) => {
  buzzStore.clearError();
  // Construct params object for the store actions
  const fetchParams = {
    page: page,
    pageSize: 10, // Or your desired page size
    sort: currentSort.value,
  };

  try {
    if (selectedCategorySlug.value) {
      // Pass slug and params object
      await buzzStore.fetchPostsByCategory(
        selectedCategorySlug.value,
        fetchParams
      );
    } else {
      // Pass params object
      await buzzStore.fetchAllPosts(fetchParams);
    }
    // Update currentPage ref AFTER successful fetch
    currentPage.value = page;
  } catch (fetchError) {
    console.error("Error fetching blog data:", fetchError);
    // Error is handled by the store, but you could add component-specific handling here
  }
};

// Handler for filter/sort changes

const handleFilterChange = () => {
  // Reset to page 1 and fetch when sort changes
  fetchBlogData(1);
};

const selectCategory = (slug: string | null) => {
  if (selectedCategorySlug.value !== slug) {
    selectedCategorySlug.value = slug;
    fetchBlogData(1); // Fetch page 1 for the new category
  }
};

// Go to a specific page
const goToPage = (page: number) => {
  if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
    fetchBlogData(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};

// Lifecycle Hooks & Watchers
onMounted(async () => {
  if (categories.value.length === 0) {
    try {
      await buzzStore.fetchAllCategories();
    } catch (catError) {
      console.error("Error fetching categories:", catError);
    }
  }
  await fetchBlogData(1); // Fetch initial data for page 1
});

// Watch for changes that require refetching (excluding currentPage, handled by goToPage)
// Note: We don't need the complex watch from before as filter changes trigger fetch directly
// watch([selectedCategorySlug, currentSort], () => {
//   fetchBlogData(1); // Reset to page 1 when filters change
// });
// The @change handlers on the select elements now trigger the fetch via handleFilterChange

// --- Pagination Logic ---
const paginationRange = computed(() => {
  const current = currentPage.value;
  const last = totalPages.value;
  const delta = 1; // How many pages to show around the current page (adjust as needed)
  const range: (number | string)[] = [];
  const rangeWithDots: (number | string)[] = [];
  let l: number | null = null;

  // Define boundaries for the range around the current page
  const left = Math.max(2, current - delta); // Ensure left boundary is at least 2
  const right = Math.min(last - 1, current + delta); // Ensure right boundary is at most last - 1

  range.push(1); // Always show the first page

  // Determine if left ellipsis is needed
  if (left > 2) {
    range.push("...");
  }

  // Add pages within the calculated range
  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  // Determine if right ellipsis is needed
  if (right < last - 1) {
    range.push("...");
  }

  if (last > 1) {
    range.push(last); // Always show the last page if it's different from the first
  }

  // The previous logic for adding dots between gaps is no longer needed with this approach
  return range;
});

// Dynamic Page Title based on Category
const pageTitle = computed(() => {
  if (selectedCategorySlug.value) {
    const category = categories.value.find(
      // Search actual categories data
      (cat) => cat.attributes.slug === selectedCategorySlug.value
    );
    return category ? `${category.attributes.name} Posts - Buzz` : "Buzz Posts";
  }
  return "Buzz - Latest Academic Updates and Insights";
});

// Set page metadata
useHead({
  title: pageTitle,
  meta: [
    {
      name: "description",
      content:
        "Stay informed with the latest academic news, insights, and announcements from our community. Filter by category and sort posts.",
    },
  ],
});
</script>

<style scoped>
nav button + button,
nav button + span,
nav span + button {
  margin-left: -1px; /* Overlap borders */
}

/* Ensure focus rings are visible */
button:focus-visible,
select:focus-visible {
  outline: 2px solid #2563eb; /* Example blue focus ring */
  outline-offset: 1px;
  z-index: 20; /* Bring focused element above siblings */
}
</style>
